import { AlertErrorCircle } from '@/assets/icons'
import { Modal, ModalContent, ModalFooterWithActions, ModalFooterWithActionsType } from '@/components/modal'

interface DeleteItemsDialogProps {
  open: boolean
  files: { id: string; name: string }[]
  folders: { id: string; name: string }[]
  onClose: () => void
  onConfirm: () => Promise<void>
  isDeleting: boolean
  error: string | null
}

/**
 * DeleteItemsDialog
 *
 * Unified confirmation dialog for deleting files, folders, or both.
 * Receives all state from parent/hook.
 */
export function DeleteItemsDialog({ open, files, folders, onClose, onConfirm, isDeleting, error }: DeleteItemsDialogProps) {
  if (!open || (files.length === 0 && folders.length === 0)) return null

  const fileCount = files.length
  const folderCount = folders.length
  const total = fileCount + folderCount

  const description =
    folderCount > 0 && fileCount > 0
      ? `${folderCount === 1 ? '1 folder' : `${folderCount} folders`} and ${fileCount === 1 ? '1 file' : `${fileCount} files`} will be deleted permanently.`
      : folderCount > 0
      ? folderCount === 1
        ? '1 folder will be deleted permanently.'
        : `${folderCount} folders will be deleted permanently.`
      : fileCount === 1
      ? '1 file will be deleted permanently.'
      : `${fileCount} files will be deleted permanently.`

  return (
    <Modal open onOpenChange={(isOpen) => !isOpen && onClose()}>
      <ModalContent
        title={total === 1 ? 'Delete 1 item?' : `Delete ${total} items?`}
        width="md"
        height="max-md"
        footer={
          <ModalFooterWithActions
            type={ModalFooterWithActionsType.DESTRUCTIVE}
            secondaryAction={{ label: 'Cancel', onClick: onClose }}
            primaryAction={{
              label: 'Delete',
              onClick: onConfirm,
              loading: isDeleting ? 'loading' : undefined,
            }}
          />
        }
      >
        <p className="text-brand-neutral-700 mb-4">{description}</p>
        {error && (
          <p className="p-2 flex items-center gap-1 text-xs text-red-800 rounded bg-red-50">
            <AlertErrorCircle className="shrink-0 w-3.5 h-3.5" />
            {error}
          </p>
        )}
      </ModalContent>
    </Modal>
  )
}
